import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    Spinner,
    Table,
} from "reactstrap";
import { toast } from "react-toastify";

import Loaders from "components/Common/Loaders";
import SmePerformanceReportTable from "components/Common/SmePerformanceReportTable";
import { StaticFilterSelect } from "components/Common/AsyncSearchSelects";
import {
    DEFAULT_MONTHS,
    buildDrillDownMap,
    buildTableColumns,
    extractReportPayload,
    getDrillDownItems,
    mapUsersToTableRows,
} from "helpers/monthWisePerformanceReportHelpers";
import { submitCallerPerformanceReportFilterData } from "model/CallerPerformanceReportModel";
import { fetchMonthSummaryYearOptions } from "model/MonthWiseSummaryReportModel";

const CallerPerformanceReport = () => {
    const [yearOptions, setYearOptions] = useState([]);
    const [selectedYear, setSelectedYear] = useState("");
    const [loadingYears, setLoadingYears] = useState(false);
    const [loading, setLoading] = useState(false);
    const [monthDefinitions, setMonthDefinitions] = useState(DEFAULT_MONTHS);
    const [reportData, setReportData] = useState([]);
    const [drillDownMap, setDrillDownMap] = useState(new Map());
    const [drillDownModalOpen, setDrillDownModalOpen] = useState(false);
    const [drillDownModalContext, setDrillDownModalContext] = useState(null);

    const tableColumns = useMemo(
        () => buildTableColumns(monthDefinitions),
        [monthDefinitions]
    );
    const filteredData = useMemo(() => reportData, [reportData]);

    useEffect(() => {
        const loadYearOptions = async () => {
            setLoadingYears(true);
            try {
                const options = await fetchMonthSummaryYearOptions();
                const normalizedOptions = Array.isArray(options) ? options : [];
                setYearOptions(normalizedOptions);

                const currentYear = String(new Date().getFullYear());
                const defaultYear =
                    normalizedOptions.find(
                        (option) => String(option?.value) === currentYear
                    )?.value ?? normalizedOptions[0]?.value ?? "";
                setSelectedYear(defaultYear ? String(defaultYear) : "");
            } finally {
                setLoadingYears(false);
            }
        };

        void loadYearOptions();
    }, []);

    const handleYearChange = (option) => {
        setSelectedYear(option?.value ? String(option.value) : "");
    };

    const handleSearch = async () => {
        const year = Number(selectedYear);
        if (!Number.isFinite(year) || year <= 0) {
            toast.error("Please select a year");
            return;
        }

        setLoading(true);
        try {
            const response = await submitCallerPerformanceReportFilterData({ year });

            if (response == null || response?.success === false) {
                setReportData([]);
                setMonthDefinitions(DEFAULT_MONTHS);
                setDrillDownMap(new Map());
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            const { months, users, drillDowns } = extractReportPayload(response);
            setMonthDefinitions(months);
            setDrillDownMap(buildDrillDownMap(drillDowns));
            setReportData(mapUsersToTableRows(users, months));
        } catch (error) {
            toast.error(error?.message || "Unable to load caller performance report");
            setReportData([]);
            setMonthDefinitions(DEFAULT_MONTHS);
            setDrillDownMap(new Map());
        } finally {
            setLoading(false);
        }
    };

    const handleMonthCellClick = (context = {}) => {
        const drillDownKey = String(context?.drillDownKey ?? "").trim();
        if (!drillDownKey) return;

        const drillDownEntry = drillDownMap.get(drillDownKey);
        setDrillDownModalContext({
            drillDownKey,
            userName: context?.userName || "",
            statusLabel: context?.statusLabel || "",
            monthLabel: context?.monthLabel || "",
            entry: drillDownEntry,
            items: getDrillDownItems(drillDownEntry),
        });
        setDrillDownModalOpen(true);
    };

    const closeDrillDownModal = () => {
        setDrillDownModalOpen(false);
        setDrillDownModalContext(null);
    };

    return (
        <React.Fragment>
            <div className="page-content caller-performance-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Caller Performance Report</h5>
                            <hr />

                            <div className="upper-filter-bar manage-user-filter-strip">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={4}>
                                        <span className="manage-user-filter-label">Select Year</span>
                                        <StaticFilterSelect
                                            options={yearOptions}
                                            placeholder="Select Year"
                                            value={selectedYear}
                                            onChange={handleYearChange}
                                            isClearable={false}
                                            isDisabled={loadingYears}
                                            isSearchable
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={handleSearch}
                                            disabled={loading || loadingYears || !selectedYear}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-1" />
                                                    Searching…
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-search me-1" aria-hidden="true" />
                                                    Search
                                                </>
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <SmePerformanceReportTable
                                    columns={tableColumns}
                                    data={filteredData}
                                    onMonthCellClick={handleMonthCellClick}
                                />
                            )}
                        </CardBody>
                    </Card>

                    <Modal
                        isOpen={drillDownModalOpen}
                        toggle={closeDrillDownModal}
                        size="xl"
                        backdrop="static"
                        centered
                        zIndex={5000}
                        className="modal-full-height"
                    >
                        <ModalHeader toggle={closeDrillDownModal}>
                            {drillDownModalContext?.statusLabel || "Details"}
                        </ModalHeader>
                        <ModalBody>
                            {drillDownModalContext?.userName ? (
                                <p className="mb-2">
                                    <strong>User:</strong> {drillDownModalContext.userName}
                                </p>
                            ) : null}
                            {drillDownModalContext?.monthLabel ? (
                                <p className="mb-3">
                                    <strong>Month:</strong> {drillDownModalContext.monthLabel}
                                </p>
                            ) : null}
                            {drillDownModalContext?.items?.length ? (
                                <div className="table-responsive">
                                    <Table bordered className="align-middle mb-0">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Client</th>
                                                <th>Contact Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {drillDownModalContext.items.map((item, index) => (
                                                <tr key={item?.id ?? item?.clientId ?? index}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        {item?.clientName ??
                                                            item?.name ??
                                                            item?.candidateName ??
                                                            item?.title ??
                                                            "—"}
                                                    </td>
                                                    <td>
                                                        {item?.recConName ??
                                                            "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <p className="text-muted mb-0">No drill-down details found.</p>
                            )}
                        </ModalBody>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    );
};

CallerPerformanceReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(CallerPerformanceReport);
